import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AdminLangBar from "./AdminLangBar";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Plus, X } from "lucide-react";
import RichTextEditor from "../../components/RichTextEditor";
import ImageUploader from "../../components/ImageUploader";

const inputCls =
  "w-full bg-transparent border-b border-[#E5DFD5] py-2.5 text-sm focus:outline-none focus:border-[#C5A880]";

const FieldLabel = ({ children }) => (
  <label className="text-[10px] tracking-[0.25em] uppercase text-[#73706B] mb-2 block">
    {children}
  </label>
);

function ListEditor({ value, onChange, placeholder, testId }) {
  const list = value || [];
  return (
    <div data-testid={testId} className="space-y-2">
      {list.map((v, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            value={v}
            onChange={(e) => {
              const next = [...list];
              next[i] = e.target.value;
              onChange(next);
            }}
            placeholder={placeholder}
            className={inputCls}
          />
          <button
            type="button"
            onClick={() => onChange(list.filter((_, idx) => idx !== i))}
            className="text-[#73706B] hover:text-red-700 p-1"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...list, ""])}
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C5A880] hover:text-[#1A1A1A]"
      >
        <Plus className="w-3 h-3" strokeWidth={1.5} /> Add
      </button>
    </div>
  );
}

function StatsEditor({ value, onChange }) {
  const list = value || [];
  const update = (i, key, val) => {
    const next = [...list];
    next[i] = { ...next[i], [key]: val };
    onChange(next);
  };
  return (
    <div className="space-y-2">
      {list.map((s, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <input
            value={s.k || ""}
            onChange={(e) => update(i, "k", e.target.value)}
            placeholder="Number"
            className={`${inputCls} col-span-4`}
          />
          <input
            value={s.l || ""}
            onChange={(e) => update(i, "l", e.target.value)}
            placeholder="Label"
            className={`${inputCls} col-span-7`}
          />
          <button
            type="button"
            onClick={() => onChange(list.filter((_, idx) => idx !== i))}
            className="text-[#73706B] hover:text-red-700 p-1 col-span-1"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...list, { k: "", l: "" }])}
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C5A880] hover:text-[#1A1A1A]"
      >
        <Plus className="w-3 h-3" strokeWidth={1.5} /> Add stat
      </button>
    </div>
  );
}

export default function AdminPageEdit() {
  const { key } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lang, setLang] = useState("en");
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    setPage(null);
    (async () => {
      try {
        const { data } = await api.get(`/admin/pages/${key}`, { params: { lang } });
        setPage(data);
      } catch {
        // create blank scaffold if not found
        setPage({ key, title: "", eyebrow: "", heading: "", intro: "", seo: { title: "", description: "" } });
      }
    })();
  }, [key, lang, reloadTick]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { key: _, ...payload } = page;
      await api.put(`/admin/pages/${key}`, payload, { params: { lang } });
      toast.success("Saved");
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!page)
    return (
      <div className="p-10">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A880]" strokeWidth={1.5} />
      </div>
    );

  const hasHighlights = "highlights" in page || /^explore-/.test(key);
  const hasGallery = "gallery" in page || /^explore-/.test(key);
  const hasStats = "stats" in page || key === "about";
  const hasContactInfo = key === "contact";
  const hasCta = "cta_label" in page || /^explore-/.test(key) || key === "about";
  const hasCategoryFilter = "category_filter" in page || /^category-/.test(key);

  return (
    <div className="p-10 lg:p-16 max-w-[1000px]" data-testid="admin-page-edit">
      <Link
        to="/admin/pages"
        className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A] mb-8"
      >
        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> All Pages
      </Link>
      <h1
        className="text-4xl lg:text-5xl font-light mb-2"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {page.title || key}
      </h1>
      <p className="text-sm text-[#73706B] mb-12">
        Editing site page: <code className="text-[#1A1A1A]">{key}</code>
      </p>

      <AdminLangBar lang={lang} onChange={setLang} kind="page" docKey={key} disabled={false} onTranslated={() => setReloadTick((n) => n + 1)} />

      <form onSubmit={save} className="space-y-10">
        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">
            — Hero
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FieldLabel>Eyebrow (small caps)</FieldLabel>
              <input
                value={page.eyebrow || ""}
                onChange={(e) => setPage({ ...page, eyebrow: e.target.value })}
                className={inputCls}
                data-testid="page-field-eyebrow"
              />
            </div>
            <div>
              <FieldLabel>Page title (admin display)</FieldLabel>
              <input
                value={page.title || ""}
                onChange={(e) => setPage({ ...page, title: e.target.value })}
                className={inputCls}
                data-testid="page-field-title"
              />
            </div>
            <div className="md:col-span-2">
              <FieldLabel>Heading (use &lt;em&gt; for accents)</FieldLabel>
              <textarea
                value={page.heading || ""}
                onChange={(e) => setPage({ ...page, heading: e.target.value })}
                rows={2}
                className={`${inputCls} resize-none`}
                data-testid="page-field-heading"
              />
            </div>
          </div>
          {hasGallery && (
            <ImageUploader
              label="Hero image"
              value={page.hero_image || ""}
              onChange={(v) => setPage({ ...page, hero_image: v })}
              testId="page-hero-image"
            />
          )}
        </section>

        <section className="space-y-6">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">
            — Body
          </h2>
          <div>
            <FieldLabel>Intro (rich text)</FieldLabel>
            <RichTextEditor
              value={page.intro || ""}
              onChange={(v) => setPage({ ...page, intro: v })}
            />
          </div>
        </section>

        {hasHighlights && (
          <section className="space-y-4">
            <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Highlights</h2>
            <ListEditor
              value={page.highlights || []}
              onChange={(v) => setPage({ ...page, highlights: v })}
              placeholder="e.g. Two seawater pools"
              testId="page-highlights"
            />
          </section>
        )}

        {hasGallery && (
          <section className="space-y-4">
            <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Gallery</h2>
            <div className="space-y-3">
              {(page.gallery || []).map((g, i) => (
                <ImageUploader
                  key={i}
                  label={`Gallery image ${i + 1}`}
                  value={g}
                  onChange={(v) => {
                    const next = [...(page.gallery || [])];
                    next[i] = v;
                    setPage({ ...page, gallery: next });
                  }}
                  testId={`page-gallery-${i}`}
                />
              ))}
              <button
                type="button"
                onClick={() => setPage({ ...page, gallery: [...(page.gallery || []), ""] })}
                className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#C5A880] hover:text-[#1A1A1A]"
              >
                <Plus className="w-3 h-3" strokeWidth={1.5} /> Add gallery image
              </button>
            </div>
          </section>
        )}

        {hasStats && (
          <section className="space-y-4">
            <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— Stats</h2>
            <StatsEditor
              value={page.stats || []}
              onChange={(v) => setPage({ ...page, stats: v })}
            />
          </section>
        )}

        {hasCategoryFilter && (
          <section className="space-y-4">
            <FieldLabel>Category filter (only show developments in this category)</FieldLabel>
            <select
              value={page.category_filter || ""}
              onChange={(e) => setPage({ ...page, category_filter: e.target.value })}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="frontline-golf">frontline-golf</option>
              <option value="sea-view">sea-view</option>
              <option value="apartments">apartments</option>
              <option value="penthouses">penthouses</option>
              <option value="villas">villas</option>
            </select>
          </section>
        )}

        {hasContactInfo && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] md:col-span-2">
              — Contact details
            </h2>
            <div>
              <FieldLabel>Email</FieldLabel>
              <input
                value={page.email || ""}
                onChange={(e) => setPage({ ...page, email: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <FieldLabel>Phone</FieldLabel>
              <input
                value={page.phone || ""}
                onChange={(e) => setPage({ ...page, phone: e.target.value })}
                className={inputCls}
              />
            </div>
            <div className="md:col-span-2">
              <FieldLabel>Address</FieldLabel>
              <textarea
                value={page.address || ""}
                onChange={(e) => setPage({ ...page, address: e.target.value })}
                rows={2}
                className={`${inputCls} resize-none`}
              />
            </div>
          </section>
        )}

        {hasCta && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] md:col-span-2">
              — Call to action
            </h2>
            <div>
              <FieldLabel>CTA label</FieldLabel>
              <input
                value={page.cta_label || ""}
                onChange={(e) => setPage({ ...page, cta_label: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <FieldLabel>CTA link</FieldLabel>
              <input
                value={page.cta_link || ""}
                onChange={(e) => setPage({ ...page, cta_link: e.target.value })}
                className={inputCls}
              />
            </div>
          </section>
        )}

        <section className="space-y-4">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880]">— SEO</h2>
          <FieldLabel>SEO title</FieldLabel>
          <input
            value={page.seo?.title || ""}
            onChange={(e) =>
              setPage({ ...page, seo: { ...(page.seo || {}), title: e.target.value } })
            }
            className={inputCls}
          />
          <FieldLabel>SEO description</FieldLabel>
          <textarea
            value={page.seo?.description || ""}
            onChange={(e) =>
              setPage({ ...page, seo: { ...(page.seo || {}), description: e.target.value } })
            }
            rows={2}
            className={`${inputCls} resize-none`}
          />
        </section>

        <div className="flex items-center gap-4 border-t border-[#E5DFD5] pt-8">
          <button
            type="submit"
            disabled={saving}
            data-testid="page-save"
            className="bg-[#1A1A1A] text-[#F9F6F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A880] disabled:opacity-60 transition-colors inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />}
            {saving ? "Saving" : "Save Changes"}
          </button>
          <Link
            to="/admin/pages"
            className="text-[11px] tracking-[0.25em] uppercase text-[#73706B] hover:text-[#1A1A1A]"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
